import {useEffect, useState} from 'react';
import Highcharts, {HTMLDOMElement} from 'highcharts';

import {ChartSeriesType} from '../common/kpiUtils';
import {KpiSingleValueColumnWidgetConfigSettings} from './types';

const digestSettings = (settings: KpiSingleValueColumnWidgetConfigSettings, value: number | null | undefined): any => {
	const {unit, zones, label, kpiName} = settings;
	const chartZones = zones.map((zone, index) => {
		if (index < zones.length - 1) {
			return zone;
		} else {
			// the last zone is always until infinity...
			return {
				color: zone.color
			};
		}
	});
	return {
		chart: {
			height: null
		},
		title: {
			text: undefined
		},
		credits: {
			enabled: false
		},
		plotOptions: {
			series: {
				animation: true
			}
		},
		tooltip: {
			// @ts-ignore
			formatter: function () {
				// @ts-ignore
				return `${this.series.name}: ${this.y} ${this.series.options.unit || ''}`;
			}
		},
		xAxis: {
			visible: false
		},
		yAxis: {
			min: 0,
			max: !value || isNaN(value) || value <= 100 ? 100 : undefined,
			title: {
				enabled: false
			},
			labels: {
				// @ts-ignore
				formatter: function () {
					// @ts-ignore
					return `${this.value} ${unit || ''}`;
				}
			}
		},
		series: [
			{
				data: [value == null || isNaN(value) ? 0 : value],
				type: ChartSeriesType.COLUMN,
				name: label || kpiName,
				zones: chartZones,
				unit: unit,
				color: chartZones.length ? chartZones[0].color : '#000000' // need to set this for the legend
			}
		],
		accessibility: {
			enabled: false
		}
	};
};

const useSingleValueColumnChart = (
	value: number | null | undefined,
	settings: KpiSingleValueColumnWidgetConfigSettings,
	editMode: boolean
): ((node: HTMLDOMElement | null | undefined) => void) => {
	const [chart, setChart] = useState<Highcharts.Chart | null | undefined>(null);
	const [chartNode, setChartNode] = useState<HTMLDOMElement | null | undefined>(null);
	useEffect(() => {
		if (chartNode && !editMode) {
			if (chart) {
				if (chart.series.length) {
					chart.series[0].update(
						{
							data: [!value || isNaN(value) ? 0 : value],
							type: 'column'
						},
						false
					);

					if (chart.yAxis && chart.yAxis.length) {
						chart.yAxis[0].update(
							{
								max: !value || isNaN(value) || value <= 100 ? 100 : undefined
							},
							false
						);
					}

					chart.redraw();
				}
			} else {
				const chartSettings = digestSettings(settings, value);
				if (chartNode) {
					const chart = Highcharts.chart(chartNode, chartSettings);
					setChart(chart);
					// we need to do this here since the height of the chart is not rendered correctly
					// despite the chart's config being the same than single period
					setTimeout(() => chart.reflow(), 200);
				}
			}
		}
	}, [chart, chartNode, editMode, settings, value]);
	return setChartNode;
};

export default useSingleValueColumnChart;
